<script setup lang="ts">
import type { CmsElementProductDescriptionReviews } from "@shopware/composables";
import { useCmsTranslations } from "@shopware/composables";
import { getTranslatedProperty } from "@shopware/helpers";
import { getProductName } from "~/components/utilities";
import { defu } from "defu";
import { computed, ref } from "vue";
import { useProduct, useShopwareContext, useUser } from "#imports";
import type { Schemas } from "#shopware";

const props = defineProps<{
  content: CmsElementProductDescriptionReviews;
}>();

type Translations = {
  product: {
    description: string;
    reviews: string;
    messages: {
      reviewAdded: string;
    };
  };
};

let translations: Translations = {
  product: {
    description: "Description",
    reviews: "Reviews",
    messages: {
      reviewAdded: "Thank you for submitting your review",
    },
  },
};
translations = defu(useCmsTranslations(), translations) as Translations;

const currentTab = ref<number>(1);
const { product } = useProduct(props.content.data?.product);

const description = computed(() =>
  getTranslatedProperty(product.value, "description")
);

const toggleTabs = (tabNumber: number) => {
  currentTab.value = tabNumber;
};

const reviews: Ref<Schemas["ProductReview"][]> = ref([]);

const { apiClient } = useShopwareContext();
const { isLoggedIn } = useUser();
const reviewAdded = ref(false);
const { setReviews } = useProductReviews();
const { groupedProperties, measurements } = useProductDetails(product);

const productVideos = computed(() => {
  const youtubeIds =
    product.value?.translated?.customFields?.zenit_stratus_youtube_ids?.split(
      ","
    );
  const vimeoIds =
    product.value?.translated?.customFields?.zenit_stratus_vimeo_ids?.split(
      ","
    );

  if (!youtubeIds && !vimeoIds) {
    return [];
  }

  return [
    ...(youtubeIds ?? []).map((youtubeId: string) => {
      const [, title, id = youtubeId] = youtubeId.match(/\[(.*?)\](.*)/) || [];

      return {
        id,
        title: title || undefined,
        url: `https://www.youtube.com/embed/${id}`,
      };
    }),
    ...(vimeoIds ?? []).map((vimeoId: string) => {
      const [, id, title = vimeoId] = vimeoId.match(/\[(.*?)\](.*)/) || [];
      return {
        id,
        title: title || undefined,
        url: `https://player.vimeo.com/video/${id}`,
      };
    }),
  ];
});

const customTab1Title = computed(
  () => product.value?.translated?.customFields?.zenit_stratus_custom_tab1_title
);

const customTab1Content = computed(
  () => product.value?.translated?.customFields?.zenit_stratus_custom_tab1_text
);

const customTab2Title = computed(
  () => product.value?.translated?.customFields?.zenit_stratus_custom_tab2_title
);

const customTab2Content = computed(
  () => product.value?.translated?.customFields?.zenit_stratus_custom_tab2_text
);

const fetchReviews = async () => {
  const reviewsResponse = await apiClient.invoke(
    "readProductReviews post /product/{productId}/reviews",
    {
      pathParams: { productId: product.value.id },
    }
  );
  reviews.value = reviewsResponse.data.elements || [];
  setReviews(reviews.value);
};

const handleReviewAdded = () => {
  reviewAdded.value = true;
  fetchReviews();
};

const activeAccordion = ref("description");

const toggleAccordion = (id: string) => {
  activeAccordion.value = activeAccordion.value === id ? "" : id;
};

const onEnter = (el: Element) => {
  const height = el.scrollHeight;
  const animation = el.animate(
    [
      { height: "0", opacity: 0 },
      { height: `${height}px`, opacity: 1 },
    ],
    {
      duration: 300,
      easing: "ease-out",
    }
  );
  animation.onfinish = () => {
    (el as HTMLElement).style.height = "auto";
  };
};

const onLeave = (el: Element) => {
  const height = el.scrollHeight;
  (el as HTMLElement).style.height = `${height}px`;

  void el.getBoundingClientRect().width;

  const animation = el.animate(
    [
      { height: `${height}px`, opacity: 1 },
      { height: "0", opacity: 0 },
    ],
    {
      duration: 300,
      easing: "ease-in",
    }
  );

  (el as HTMLElement).style.height = "0";

  animation.onfinish = () => {
    (el as HTMLElement).style.height = "";
  };
};

onMounted(async () => {
  if (props.content.data?.reviews?.elements) {
    reviews.value = props.content.data.reviews.elements;
    setReviews(reviews.value);
  }
});
</script>
<template>
  <div class="md:hidden prodAccrod">
    <div class="accordian-wrapper w-full bg-cust_cream py-4 px-2 rounded-3xl">
      <div class="accordian-item py-4">
        <div
          class="accordian-head"
          :class="{ active: activeAccordion === 'description' }"
          @click="toggleAccordion('description')"
        >
          <h5
            class="normal-case font-bold text-lg md:text-xl after:font-font-awesome leading-6 flex items-start justify-between cursor-pointer font-redit_sans_con"
          >
            {{ $t("product.description") }}
          </h5>
        </div>
        <transition name="slide" @enter="onEnter" @leave="onLeave">
          <div
            v-show="activeAccordion === 'description'"
            class="accordian-details leading-6 pt-3 text-base md:text-lg [&_b]:font-[600] listing"
          >
            <div v-html="product?.translated?.description"></div>
          </div>
        </transition>
      </div>
      <div
        v-if="
          Object.keys(groupedProperties).length > 0 || measurements.length > 0
        "
        class="accordian-item py-4"
      >
        <div
          class="accordian-head"
          :class="{ active: activeAccordion === 'details' }"
          @click="toggleAccordion('details')"
        >
          <h5
            class="normal-case font-bold text-lg md:text-xl after:font-font-awesome leading-6 flex items-start justify-between cursor-pointer font-redit_sans_con"
          >
            {{ $t("product.details") }}
          </h5>
        </div>
        <transition name="slide" @enter="onEnter" @leave="onLeave">
          <div
            v-show="activeAccordion === 'details'"
            class="accordian-details leading-6 pt-3 text-base md:text-lg [&_b]:font-[600] listing"
          >
            <table class="w-full mt-2">
              <tbody>
                <tr
                  v-for="group in groupedProperties"
                  :key="group.group.id"
                  class="flex items-center justify-between lg:justify-normal pb-5 mb-5 border-b border-cust_green border-opacity-25"
                >
                  <th
                    class="font-medium lg:min-w-[35%] lg:w-[35%] mr-5 p-0 text-left"
                  >
                    <span class="align-middle">
                      {{ group.group?.translated?.name }}
                    </span>
                  </th>
                  <td class="text-right lg:text-left">
                    <template
                      v-for="(property, index) in group.properties"
                      :key="property.id"
                    >
                      {{ property?.translated?.name
                      }}<span v-if="index !== group.properties?.length - 1"
                        >,
                      </span>
                    </template>
                  </td>
                </tr>
                <tr
                  v-for="measurement in measurements"
                  :key="measurement.label"
                  class="flex items-center justify-between lg:justify-normal pb-5 mb-5 border-b border-cust_green border-opacity-25"
                >
                  <th
                    class="font-medium lg:min-w-[35%] lg:w-[35%] mr-5 p-0 text-left"
                  >
                    <span class="align-middle">
                      {{ measurement.label }}
                    </span>
                  </th>
                  <td class="text-right lg:text-left">
                    {{ measurement.value }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </transition>
      </div>
      <div v-if="productVideos.length > 0" class="accordian-item py-4">
        <div
          class="accordian-head"
          :class="{ active: activeAccordion === 'video' }"
          @click="toggleAccordion('video')"
        >
          <h5
            class="normal-case font-bold text-lg md:text-xl after:font-font-awesome leading-6 flex items-start justify-between cursor-pointer font-redit_sans_con"
          >
            {{ $t("product.video") }}
          </h5>
        </div>
        <transition name="slide" @enter="onEnter" @leave="onLeave">
          <div
            v-show="activeAccordion === 'video'"
            class="accordian-details leading-6 pt-3 text-base md:text-lg [&_b]:font-[600] listing"
          >
            <div class="flex gap-4 justify-between flex-wrap">
              <template v-for="video in productVideos">
                <div class="w-full">
                  <p class="font-medium mb-3" v-if="video.title">
                    {{ video.title }}
                  </p>
                  <div class="relative pb-[56.25%] h-0">
                    <iframe
                      :src="video.url"
                      class="absolute top-0 left-0 w-full h-full"
                    />
                  </div>
                </div>
              </template>
            </div>
          </div>
        </transition>
      </div>
      <div v-if="customTab1Title" class="accordian-item py-4">
        <div
          class="accordian-head"
          :class="{ active: activeAccordion === customTab1Title }"
          @click="toggleAccordion(customTab1Title)"
        >
          <h5
            class="normal-case font-bold text-lg md:text-xl after:font-font-awesome leading-6 flex items-start justify-between cursor-pointer font-redit_sans_con"
          >
            {{ customTab1Title }}
          </h5>
        </div>
        <transition name="slide" @enter="onEnter" @leave="onLeave">
          <div
            v-show="activeAccordion === customTab1Title"
            class="accordian-details leading-6 pt-3 text-base md:text-lg [&_b]:font-[600] listing"
          >
            <div v-html="customTab1Content"></div>
          </div>
        </transition>
      </div>
      <div v-if="customTab2Title" class="accordian-item py-4">
        <div
          class="accordian-head"
          :class="{ active: activeAccordion === customTab2Title }"
          @click="toggleAccordion(customTab2Title)"
        >
          <h5
            class="normal-case font-bold text-lg md:text-xl after:font-font-awesome leading-6 flex items-start justify-between cursor-pointer font-redit_sans_con"
          >
            {{ customTab2Title }}
          </h5>
        </div>
        <transition name="slide" @enter="onEnter" @leave="onLeave">
          <div
            v-show="activeAccordion === customTab2Title"
            class="accordian-details leading-6 pt-3 text-base md:text-lg [&_b]:font-[600] listing"
          >
            <div v-html="customTab2Content"></div>
          </div>
        </transition>
      </div>
      <div class="accordian-item py-4">
        <div
          class="accordian-head"
          :class="{ active: activeAccordion === 'reviews' }"
          @click="toggleAccordion('reviews')"
        >
          <h5
            class="normal-case font-bold text-lg md:text-xl after:font-font-awesome leading-6 flex items-start justify-between cursor-pointer font-redit_sans_con"
          >
            {{ $t("product.reviews.label") }}
          </h5>
        </div>
        <transition name="slide" @enter="onEnter" @leave="onLeave">
          <div
            v-show="activeAccordion === 'reviews'"
            class="accordian-details leading-6 pt-3 text-base md:text-lg"
          >
            <ProductReviews :product="product" :reviews="reviews" />
            <ProductReviewsForm
              v-if="isLoggedIn && !reviewAdded"
              :product-id="product.id"
              @success="handleReviewAdded"
            />
            <div v-if="reviewAdded">
              {{ $t("product.messages.reviewAdded") }}
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>
